import { api } from '../../../api/client';

/**
 * Inventory Management → Alert — shared client for the three alert grids.
 *
 * Division scope is NOT a parameter here: the shared axios client sends
 * `X-Division-Id` on every request and the API resolves it through
 * `IDivisionContext` (§18.3).  That is deliberate — legacy filtered only the
 * Expiration Alert view by division, so passing a division per call is exactly
 * the thing that let Stock Watch / Stock Alert leak other divisions' items.
 */

/** Server read cap (mirrors `IcmAlertReader.ClampTake`). The grids fetch one
 *  page of this size and page client-side. */
export const ALERT_PAGE_CAP = 2000;

export interface AlertEnvelope<T> {
  rows: T[];
  /** True total behind the criteria — may exceed `rows.length` at the cap. */
  total: number;
  /** Expiration Alert only: lots already past expiry, counted server-side over
   *  the WHOLE criteria.  Never derive this from `rows` — that page is capped,
   *  so a derived figure shrinks silently once the cap bites. */
  expiredCount: number;
}

export interface StockWatchRow {
  oid: string;
  itemName: string | null;
  itemCode: string | null;
  description: string | null;
  vendorName: string | null;
  catalog: string | null;
  stockQty: number;
  packUnit: string | null;
  storageNames: string | null;
}

export interface StockAlertRow {
  oid: string;
  itemName: string | null;
  itemCode: string | null;
  description: string | null;
  vendorName: string | null;
  vendorId: string | null;
  /** Carried with vendorId/catalog/unitPrice so "Requisition" can pre-fill a
   *  draft exactly as legacy's Requisition_ListViewEntermode auto-add does. */
  manufacturerId: string | null;
  manufacturerName: string | null;
  unitPrice: number;
  catalog: string | null;
  packUnit: string | null;
  stockQty: number;
  alertQty: number;
  storageNames: string | null;
}

export interface ExpirationAlertRow {
  oid: string;
  lt: string | null;
  itemName: string | null;
  itemCode: string | null;
  description: string | null;
  vendorName: string | null;
  expiryDate: string | null;
  daysRemaining: number;
}

type Envelope<T> = { data?: T[]; meta?: { total?: number; expiredCount?: number } };

async function get<T>(path: string, params?: Record<string, unknown>): Promise<AlertEnvelope<T>> {
  const res = await api.get(path, { params: { take: ALERT_PAGE_CAP, ...params } });
  const body = res.data as Envelope<T>;
  const rows = body.data ?? [];
  return {
    rows,
    total: body.meta?.total ?? rows.length,
    expiredCount: body.meta?.expiredCount ?? 0,
  };
}

export const fetchStockWatch = () =>
  get<StockWatchRow>('/api/v1/icm-alerts/stock-watch');

export const fetchStockAlert = () =>
  get<StockAlertRow>('/api/v1/icm-alerts/stock-alert');

export const fetchExpirationAlert = (daysAhead: number) =>
  get<ExpirationAlertRow>('/api/v1/icm-alerts/expiration-alert', { daysAhead });
